import * as React from 'react';

import { ALL, BOOSTED_SECTION_ID, Pack, RecommendationCounts, UnitProgress } from './shared';

import { requestGet, requestPut } from '../../../../../modules/request';
import { PROGRESS_REPORTS_SELECTED_CLASSROOM_ID } from '../../progress_reports/progress_report_constants';

interface Classroom {
  id: number;
  name: string;
}

interface RawUnit {
  activity_classification_id: string;
  assign_on_join?: boolean;
  activity_id: string;
  activity_name: string;
  class_name: string;
  classroom_activity_id?: string;
  classroom_id: string;
  class_size: string;
  due_date?: string;
  number_of_assigned_students?: string;
  open?: boolean;
  owned_by_current_user?: boolean;
  owner_name?: string;
  publish_date?: string;
  scheduled?: boolean;
  staggered?: boolean;
  unit_activity_created_at?: string;
  unit_activity_id?: number;
  classroom_unit_created_at?: string;
  unit_created_at: string;
  unit_id: string;
  unit_name: string;
}

export interface DiagnosticClassroom {
  id: number;
  name: string;
  diagnostics: Array<{ name: string, pre: any, post?: any }>;
}

function parsePacks(data: RawUnit[]): Pack[] {
  const packsById: Record<string, Pack> = {}

  data.forEach(row => {
    let pack = packsById[row.unit_id]
    if (!pack) {
      pack = packsById[row.unit_id] = {
        unitId: row.unit_id,
        unitName: row.unit_name,
        unitCreatedEpoch: Number(row.unit_created_at),
        open: row.open,
        ownedByCurrentUser: row.owned_by_current_user,
        ownerName: row.owner_name,
        classrooms: [],
        activities: [],
      }
    }
    if (!pack.classrooms.some(c => String(c.id) === String(row.classroom_id))) {
      pack.classrooms.push({
        id: row.classroom_id,
        name: row.class_name,
        assignOnJoin: Boolean(row.assign_on_join),
        assignedEpoch: Number(row.classroom_unit_created_at) || undefined,
        assignedStudentCount: Number(row.number_of_assigned_students ?? row.class_size),
        totalStudentCount: Number(row.class_size),
        staggered: Boolean(row.staggered),
      })
    }
    const existingActivity = pack.activities.find(a => String(a.activityId) === String(row.activity_id))
    if (existingActivity) {
      existingActivity.staggered = existingActivity.staggered || Boolean(row.staggered)
    } else {
      pack.activities.push({
        activityId: row.activity_id,
        name: row.activity_name,
        classificationId: Number(row.activity_classification_id),
        uaId: row.unit_activity_id,
        cuId: row.classroom_activity_id,
        classroomId: row.classroom_id,
        dueDate: row.due_date,
        publishDate: row.publish_date,
        scheduled: row.scheduled,
        staggered: Boolean(row.staggered),
        createdAt: row.unit_activity_created_at ?? row.unit_created_at,
      })
    }
  })

  return Object.values(packsById)
}

export function usePackData({ onLoadError }: { onLoadError: () => void }) {
  const [loaded, setLoaded] = React.useState(false)
  const [classrooms, setClassrooms] = React.useState<Classroom[]>([])
  const fetchGeneration = React.useRef(0)
  const [packs, setPacks] = React.useState<Pack[]>([])
  const [diagnosticClassrooms, setDiagnosticClassrooms] = React.useState<DiagnosticClassroom[]>([])
  const [unitProgress, setUnitProgress] = React.useState<Record<string, UnitProgress>>({})
  const [recommendationCounts, setRecommendationCounts] = React.useState<RecommendationCounts>({})
  const [selectedClassroomId, setSelectedClassroomId] = React.useState<string | number | null>(null)

  function fetchPackData() {
    const generation = (fetchGeneration.current += 1)
    const isStale = () => generation !== fetchGeneration.current

    return Promise.allSettled([
      requestGet('/teachers/units', (body) => {
        if (!isStale()) { setPacks(parsePacks(body)) }
      }, onLoadError),
      requestGet('/teachers/diagnostic_units', (body) => {
        const parsed = typeof body === 'string' ? JSON.parse(body) : body
        // swap in the roster-filtered current_* counts once at the boundary — the raw fields must not be read anywhere in V2
        const withCurrentCounts = (record: any) => (record ? {
          ...record,
          assigned_count: record.current_assigned_count ?? record.assigned_count,
          completed_count: record.current_completed_count ?? record.completed_count,
        } : record)
        if (!isStale()) {
          setDiagnosticClassrooms((parsed || []).map((classroom: DiagnosticClassroom) => ({
            ...classroom,
            diagnostics: (classroom.diagnostics || []).map(diagnostic => ({
              ...diagnostic,
              pre: withCurrentCounts(diagnostic.pre),
              post: diagnostic.post ? withCurrentCounts(diagnostic.post) : diagnostic.post,
            })),
          })))
        }
      }, onLoadError),
      requestGet('/teachers/units_progress_info', (body) => {
        if (!isStale()) {
          setUnitProgress(body.units || {})
          setRecommendationCounts(body.diagnostic_recommendations || {})
        }
      }, onLoadError),
      // a network-level throw bypasses the error callback above, so surface it from the settlement
    ]).then(results => {
      if (results.some(result => result.status === 'rejected')) { onLoadError() }
    })
  }

  function refetchPackData(onDone?: () => void) {
    fetchPackData().then(onDone)
  }

  React.useEffect(() => {
    const classroomsRequest = requestGet('/teachers/classrooms/classrooms_i_teach', (body) => {
      const fetchedClassrooms = body.classrooms || []
      setClassrooms(fetchedClassrooms)
      const urlId = Number(new URLSearchParams(window.location.search).get('classroom_id'))
      const storedId = Number(window.localStorage.getItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID))
      const initialClassroom = fetchedClassrooms.find(c => c.id === urlId) ?? fetchedClassrooms.find(c => c.id === storedId)
      setSelectedClassroomId(initialClassroom?.id ?? fetchedClassrooms[0]?.id ?? ALL)
    }, onLoadError)
    Promise.allSettled([classroomsRequest, fetchPackData()]).then(results => {
      if (results.some(result => result.status === 'rejected')) { onLoadError() }
      setLoaded(true)
    })
  }, [])

  function handleClassroomChange(classroomId: string | number) {
    window.localStorage.setItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, String(classroomId))
    setSelectedClassroomId(classroomId)
  }

  function invalidatePendingFetches() {
    fetchGeneration.current += 1
  }

  return {
    classrooms,
    diagnosticClassrooms,
    handleClassroomChange,
    invalidatePendingFetches,
    loaded,
    packs,
    recommendationCounts,
    refetchPackData,
    selectedClassroomId,
    setUnitProgress,
    unitProgress,
  }
}

export function useBoostedPacks({ unitProgress, setUnitProgress, selectedClassroomId, refetchPackData, invalidatePendingFetches, onBoostToggled }: {
  unitProgress: Record<string, UnitProgress>,
  setUnitProgress: React.Dispatch<React.SetStateAction<Record<string, UnitProgress>>>,
  selectedClassroomId: string | number | null,
  refetchPackData: () => void,
  invalidatePendingFetches: () => void,
  onBoostToggled: (message: string) => void,
}) {
  // one boost PUT per pack at a time (state only advances on the response, so a double-click
  // would re-send the same value); the success updater re-reads the current entry so a
  // mid-request refetch isn't clobbered
  const boostsInFlight = React.useRef<Set<number>>(new Set())

  // the Boosted section may only mount on the re-render the boost triggers, so the
  // scroll has to wait for the commit rather than a timer
  const pendingBoostScroll = React.useRef(false)
  React.useEffect(() => {
    if (!pendingBoostScroll.current) { return }
    pendingBoostScroll.current = false
    document.getElementById(BOOSTED_SECTION_ID)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [unitProgress])

  function classroomBoostEntry(unitId: string | number) {
    return unitProgress[String(unitId)]?.classrooms?.[String(selectedClassroomId)]
  }

  function isUnitBoosted(unitId: string | number): boolean {
    return Boolean(classroomBoostEntry(unitId)?.boosted_at)
  }

  // a rejected boost means this tab's data is stale: surface the server's reason and refetch to heal it
  function handleBoostError(classroomUnitId?: number, body?: { error?: string }) {
    if (classroomUnitId) { boostsInFlight.current.delete(classroomUnitId) }
    onBoostToggled(typeof body?.error === 'string' ? body.error : 'Boost could not be saved. Please try again.')
    refetchPackData()
  }

  function handleToggleBoost(unitId: string) {
    const entry = classroomBoostEntry(unitId)
    if (!entry?.classroom_unit_id) { return handleBoostError() }
    if (boostsInFlight.current.has(entry.classroom_unit_id)) { return }

    boostsInFlight.current.add(entry.classroom_unit_id)
    const alreadyBoosted = Boolean(entry.boosted_at)
    const request = requestPut(`/teachers/classroom_units/${entry.classroom_unit_id}/boost`, { boosted: !alreadyBoosted }, (body) => {
      invalidatePendingFetches()
      boostsInFlight.current.delete(entry.classroom_unit_id)
      setUnitProgress(previousProgress => {
        const unit = previousProgress[String(unitId)]
        const freshEntry = unit?.classrooms?.[String(selectedClassroomId)]
        if (!freshEntry) { return previousProgress }
        const updatedClassrooms = {
          ...unit.classrooms,
          [String(selectedClassroomId)]: { ...freshEntry, boosted_at: body?.boosted_at || null },
        }
        return { ...previousProgress, [String(unitId)]: { ...unit, classrooms: updatedClassrooms } }
      })
      onBoostToggled(alreadyBoosted ? 'Boost removed' : 'Activity pack boosted')
      if (!alreadyBoosted) { pendingBoostScroll.current = true }
    }, (body) => handleBoostError(entry.classroom_unit_id, body))
    request?.catch?.(() => handleBoostError(entry.classroom_unit_id))
  }

  function boostToggler(unitId: string | number) {
    if (selectedClassroomId === ALL) { return undefined }
    return function toggleBoost() { handleToggleBoost(String(unitId)) }
  }


  return { boostToggler, isUnitBoosted }
}
